import React from 'react';
import { FileText, DollarSign, RefreshCw, Clock, Shield, Scale } from 'lucide-react';

const TermsAndConditions = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-amber-50">
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12">
          <div className="text-center mb-12">
            <FileText className="w-16 h-16 text-amber-600 mx-auto mb-4" />
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Terms & Conditions</h1>
            <p className="text-gray-600 text-lg">Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 text-lg leading-relaxed mb-8">
              Welcome to Rushmore Publishing. By accessing our website or using our publishing services, you agree to be bound by these Terms and Conditions. Please read them carefully before engaging our services.
            </p>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Acceptance of Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                By using our website, submitting project inquiries, or purchasing our services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions, as well as our Privacy Policy. If you do not agree with any part of these terms, please do not use our services.
              </p>
            </div>

            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <Scale className="w-6 h-6 text-amber-600" />
                <h2 className="text-2xl font-bold text-gray-900 m-0">Copyright and Ownership</h2>
              </div>
              <p className="text-gray-700 leading-relaxed mb-4">
                At Rushmore Publishing, we believe in empowering authors. Upon final delivery and full payment of services:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>You retain full copyright and ownership of all content we create for you</li>
                <li>You have the exclusive right to use, modify, distribute, and monetize the work</li>
                <li>We do not claim any rights to your intellectual property</li>
                <li>You are recognized as the sole author of the final deliverables</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                All work produced by Rushmore Publishing is original and created specifically for you. We guarantee that our services do not infringe on any third-party copyrights.
              </p>
            </div>

            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <DollarSign className="w-6 h-6 text-amber-600" />
                <h2 className="text-2xl font-bold text-gray-900 m-0">Payment Terms</h2>
              </div>
              <p className="text-gray-700 leading-relaxed mb-4">
                Payment policies for our services:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Payment Due:</strong> Full payment or an agreed-upon deposit is required before work commences</li>
                <li><strong>Payment Methods:</strong> We accept major credit cards, PayPal, and bank transfers</li>
                <li><strong>Invoicing:</strong> Detailed invoices will be provided for all transactions</li>
                <li><strong>Taxes:</strong> You are responsible for any applicable sales tax, VAT, or other taxes</li>
                <li><strong>Late Payments:</strong> Failure to make timely payments may result in work suspension or cancellation</li>
                <li><strong>Currency:</strong> All prices are listed in USD unless otherwise specified</li>
              </ul>
            </div>

            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <Clock className="w-6 h-6 text-amber-600" />
                <h2 className="text-2xl font-bold text-gray-900 m-0">Project Timeline and Delivery</h2>
              </div>
              <p className="text-gray-700 leading-relaxed mb-4">
                We strive to meet all agreed-upon deadlines. Project timelines are established during the initial consultation and depend on:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Project scope and complexity</li>
                <li>Your responsiveness and feedback turnaround</li>
                <li>Availability of necessary materials and information</li>
                <li>Selected service package and priority level</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                While we make every effort to adhere to deadlines, unforeseen circumstances may occasionally cause delays. We will communicate proactively if timeline adjustments are needed.
              </p>
            </div>

            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <RefreshCw className="w-6 h-6 text-amber-600" />
                <h2 className="text-2xl font-bold text-gray-900 m-0">Revisions and Modifications</h2>
              </div>
              <p className="text-gray-700 leading-relaxed mb-4">
                We are committed to your satisfaction and offer revision opportunities:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Included Revisions:</strong> Each service package includes a specified number of revision rounds</li>
                <li><strong>Revision Window:</strong> Revisions must be requested within 14 days of receiving deliverables</li>
                <li><strong>Scope of Revisions:</strong> Revisions must align with the original project brief and scope</li>
                <li><strong>Additional Revisions:</strong> Requests beyond included revisions may incur additional fees</li>
                <li><strong>Major Changes:</strong> Significant changes to project direction or scope will be treated as new projects</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                <strong>Revision Turnaround Times:</strong>
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
                <li>Minor revisions: 24-48 hours</li>
                <li>Moderate revisions: 48-72 hours</li>
                <li>Extensive revisions: 3-5 business days</li>
              </ul>
            </div>

            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <Shield className="w-6 h-6 text-amber-600" />
                <h2 className="text-2xl font-bold text-gray-900 m-0">Refund Policy</h2>
              </div>
              <p className="text-gray-700 leading-relaxed mb-4">
                Our refund policy is designed to be fair to both parties:
              </p>
              
              <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3">Before Work Begins</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you cancel before any work has commenced, you may be eligible for a full refund minus any administrative fees (typically $50-100) and payment processing fees. Cancellation requests must be submitted in writing within 48 hours of purchase.
              </p>

              <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3">After Work Begins</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Once our team has begun working on your project, refunds are handled as follows:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Partial refunds:</strong> Available based on work completed, minus completed milestones</li>
                <li><strong>Dissatisfaction:</strong> We offer unlimited revisions within scope before considering refunds</li>
                <li><strong>No refunds after approval:</strong> Once you approve final deliverables, the project is considered complete</li>
              </ul>

              <h3 className="text-xl font-bold text-gray-900 mt-6 mb-3">Refund Process</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Refund requests must be submitted in writing via email</li>
                <li>Refunds are processed within 7-10 business days of approval</li>
                <li>Payment processing fees (typically 3-5%) are non-refundable</li>
                <li>Refunds are issued to the original payment method</li>
              </ul>

              <p className="text-gray-700 leading-relaxed mt-4">
                <strong>Note:</strong> Due to the custom nature of our work involving multiple team members, we cannot offer refunds once substantial progress has been made. We encourage open communication throughout the project to ensure satisfaction.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Client Responsibilities</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                To ensure project success, clients agree to:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Provide accurate, complete, and timely information required for the project</li>
                <li>Respond to requests for feedback and approvals in a timely manner</li>
                <li>Review deliverables carefully and provide specific, actionable feedback</li>
                <li>Ensure all content provided does not infringe on third-party rights</li>
                <li>Make payments according to agreed schedules</li>
                <li>Communicate concerns or issues promptly</li>
              </ul>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Confidentiality</h2>
              <p className="text-gray-700 leading-relaxed">
                We treat all client projects with strict confidentiality. Our team members sign non-disclosure agreements, and we do not share, discuss, or use your content for any purpose other than delivering your project. All files and communications are stored securely and are not disclosed to third parties without your explicit permission.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Limitation of Liability</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                To the fullest extent permitted by law:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Rushmore Publishing shall not be liable for indirect, incidental, or consequential damages</li>
                <li>Our total liability shall not exceed the amount paid for the specific service in question</li>
                <li>We are not responsible for delays or failures due to circumstances beyond our control</li>
                <li>We do not guarantee specific outcomes, sales figures, or publishing success</li>
                <li>Clients are responsible for verifying all content accuracy before publication</li>
              </ul>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Dispute Resolution</h2>
              <p className="text-gray-700 leading-relaxed">
                In the event of a dispute, we encourage direct communication to resolve issues amicably. If a resolution cannot be reached, both parties agree to participate in good-faith mediation before pursuing legal action. Any legal disputes shall be governed by the laws of the jurisdiction where Rushmore Publishing operates.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Service Modifications</h2>
              <p className="text-gray-700 leading-relaxed">
                Rushmore Publishing reserves the right to modify, suspend, or discontinue any service at any time. We will provide reasonable notice of significant changes affecting existing client projects.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Termination</h2>
              <p className="text-gray-700 leading-relaxed">
                Either party may terminate the service agreement with written notice. Upon termination, you will be billed for work completed up to that point. Rushmore Publishing reserves the right to terminate services immediately if a client violates these terms or engages in abusive behavior toward our team.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Changes to Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update these Terms and Conditions periodically. Changes will be posted on this page with an updated effective date. Continued use of our services after changes are posted constitutes acceptance of the modified terms.
              </p>
            </div>

            <div className="bg-amber-50 border-l-4 border-amber-600 p-6 rounded-r-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have questions about these Terms and Conditions, please contact us:
              </p>
              <div className="text-gray-700 space-y-2">
                <p><strong>Rushmore Publishing</strong></p>
                <p>Email: legal@rushmorepublishing.com</p>
                <p>Phone: +1 (866) 978-8498</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;